import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";

const API_BASE_URL = "https://www.frendrops.in/api";

interface LocationState {
  phoneNumber?: string;
  mobile?: string;
  phonePrefix?: string;
}

export default function OTPVerification() {
  const navigate = useNavigate();
  const location = useLocation();

  const state =
    (location.state as LocationState) || {};

  const mobile =
    state.mobile ||
    sessionStorage.getItem("login_mobile") ||
    "";

  const phonePrefix =
    state.phonePrefix ||
    sessionStorage.getItem("login_phone_prefix") ||
    "+91";

  const phoneNumber =
    state.phoneNumber ||
    `${phonePrefix}${mobile}`;

  const [otp, setOtp] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);

  const [timer, setTimer] = useState(60);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const inputRefs = useRef<
    HTMLInputElement[]
  >([]);

  const code = otp.join("");

  // Timer
  useEffect(() => {
    if (timer <= 0) return;

    const interval = window.setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [timer]);

  // Autofocus
  useEffect(() => {
    setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 150);
  }, []);

  const handleChange = (
    value: string,
    index: number
  ) => {
    const clean = value.replace(/\D/g, "");

    if (!clean) {
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
      return;
    }

    // Handle pasted OTP
    if (clean.length > 1) {
      const digits = clean.slice(0, 6).split("");

      const newOtp = [
        "",
        "",
        "",
        "",
        "",
        "",
      ];

      digits.forEach((digit, i) => {
        newOtp[i] = digit;
      });

      setOtp(newOtp);

      const focusIndex = Math.min(
        digits.length,
        5
      );

      setTimeout(() => {
        inputRefs.current[
          focusIndex
        ]?.focus();
      }, 0);

      return;
    }

    const newOtp = [...otp];
    newOtp[index] = clean;
    setOtp(newOtp);

    if (index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (
      e.key === "Backspace" &&
      otp[index] === "" &&
      index > 0
    ) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const verifyOtp = async () => {
    if (code.length !== 6) {
      setErrorMessage("Please enter the 6-digit OTP.");
      return;
    }

    setLoading(true);

    try {
      console.log("========== LOGIN START ==========");
      console.log("Phone Prefix:", phonePrefix);
      console.log("Phone Number:", mobile);
      console.log("OTP:", code);

      const response = await axios.post(
        `${API_BASE_URL}/login`,
        {
          phone_prefix: phonePrefix,
          phone_number: mobile,
          otp: code,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const backend = response.data;

      console.log(
        "LOGIN RESPONSE:",
        backend
      );

      // User does not exist
      if (
        backend?.code === "USER_NOT_FOUND" ||
        (
          backend?.success === false &&
          backend?.message ===
            "Invalid phone number"
        )
      ) {
        navigate("/signup", {
          state: {
            phone_number: mobile,
            phone_prefix: phonePrefix,
          },
        });

        return;
      }

      // Login failed
      if (backend?.success === false) {
        setErrorMessage(backend?.message || "Invalid OTP");
        return;
      }

      /*
       * Expected backend token
       */
      const token =
        backend?.token ||
        backend?.data?.token;

      const user =
        backend?.data?.user ||
        backend?.data;

      if (token) {
        localStorage.setItem(
          "token",
          token
        );

        localStorage.setItem(
          "user",
          JSON.stringify({
            ...(user || {}),
            phone_prefix: phonePrefix,
            phone_number: mobile,
          })
        );
      }

      const profileComplete =
        user?.profile_complete ??
        backend?.profile_complete ??
        backend?.data?.profile_complete;

      // Clear temporary login data
      sessionStorage.removeItem(
        "login_mobile"
      );
      sessionStorage.removeItem(
        "login_phone_prefix"
      );

      // Existing user but profile incomplete
      if (profileComplete === false) {
        navigate("/signup", {
          replace: true,
          state: {
            phone_number: mobile,
            phone_prefix: phonePrefix,
          },
        });

        return;
      }

      // Successful login
      if (token || backend?.success) {
        navigate("/productList", {
          replace: true,
        });

        return;
      }

      setErrorMessage(backend?.message || "Unable to complete login.");
    } catch (error: any) {
      console.error(
        "OTP Login Error:",
        error?.response?.data || error
      );

      setErrorMessage(error?.response?.data?.message || error?.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    if (!mobile || timer !== 0 || loading) {
      return;
    }

    setTimer(60);

    setOtp([
      "",
      "",
      "",
      "",
      "",
      "",
    ]);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/resend-otp`,
        {
          phone_prefix: phonePrefix,
          phone_number: mobile,
        },
        {
          headers: {
            "Content-Type":
              "application/json",
          },
        }
      );

      console.log(
        "RESEND RESPONSE:",
        response.data
      );

      if (response.data.success) {
        setStatusMessage(response.data.message || "OTP resent successfully.");
        setErrorMessage("");

        setTimeout(() => {
          inputRefs.current[0]?.focus();
        }, 150);
      } else {
        setErrorMessage(response.data.message || "Unable to resend OTP.");

        setTimer(0);
      }
    } catch (error: any) {
      console.error(
        "Resend OTP Error:",
        error?.response?.data ||
          error
      );

      setErrorMessage(error?.response?.data?.message || error?.message || "Unable to resend OTP.");

      setTimer(0);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f6fb] px-4 py-4">

      {/* Back */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        disabled={loading}
        className="p-2 rounded-full hover:bg-gray-200"
      >
        <ArrowLeft size={24} />
      </button>

      <div className="max-w-md mx-auto mt-5">

        <div className="bg-white rounded-2xl p-5 shadow-lg">

          <h1 className="text-xl font-bold text-gray-800">
            Verify Your Number
          </h1>

          <p className="text-gray-500 mt-1">
            We have sent an OTP to your phone.
          </p>

          <p className="font-semibold text-gray-800 mt-3">
            {phoneNumber}
          </p>

          {/* OTP */}
          <div className="flex justify-between gap-2 mt-6">

            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(element) => {
                  if (element) {
                    inputRefs.current[index] =
                      element;
                  }
                }}
                type="text"
                inputMode="numeric"
                autoComplete={
                  index === 0
                    ? "one-time-code"
                    : "off"
                }
                maxLength={1}
                value={digit}
                onChange={(e) =>
                  handleChange(
                    e.target.value,
                    index
                  )
                }
                onKeyDown={(e) =>
                  handleKeyDown(e, index)
                }
                disabled={loading}
                className="w-12 h-13 sm:w-14 sm:h-14 border border-gray-300 rounded-xl text-center text-xl font-semibold outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-100"
              />
            ))}

          </div>

          {/* Timer */}
          <p className="mt-5 text-sm text-gray-500">
            {timer > 0
              ? `Haven't received the OTP? Retry in 00:${String(
                  timer
                ).padStart(2, "0")}`
              : "Didn't receive the OTP?"}
          </p>

          {errorMessage && <p className="mt-4 text-sm text-red-600">{errorMessage}</p>}
          {statusMessage && <p className="mt-4 text-sm text-green-600">{statusMessage}</p>}

          {/* Verify */}
          <button
            type="button"
            onClick={verifyOtp}
            disabled={
              loading ||
              code.length !== 6
            }
            className={`w-full h-12 rounded-xl mt-4 text-white font-extrabold ${
              loading ||
              code.length !== 6
                ? "bg-purple-300"
                : "bg-purple-700 hover:bg-purple-800"
            }`}
          >
            {loading
              ? "Verifying..."
              : "Verify"}
          </button>

          {/* Resend */}
          <button
            type="button"
            disabled={
              timer !== 0 || loading
            }
            onClick={resendOTP}
            className={`w-full mt-3 font-bold ${
              timer === 0
                ? "text-purple-700"
                : "text-gray-400"
            }`}
          >
            Resend OTP
          </button>

        </div>

      </div>
    </div>
  );
}
